import React, {forwardRef} from 'react'
import {makeStyles} from '@mui/styles'
import PictureChatMessage from './PictureChatMessage'
import {Colors} from './../../assets/styles'
import PropTypes from 'prop-types'
import EditableTextChatMessage from './EditableTextChatMessage'
import StaffProfileImage from './StaffProfileImage'
import ResidentProfileImage from '../ResidentProfiles/ResidentProfileImage'
import {constructClassString} from '../../Utils/objectUtil'
import {fontSizeSmall} from '../../assets/styles/typography'
import {spaceSmall, spaceExtraSmall} from '../../assets/styles/spacing'
import {useSelector} from 'react-redux'
import {IconButton} from '@mui/material'
import RXRButton from '../RXRButton'
import RXRIcon from '../RXRIcon'
import {formatScheduledTime} from '../../Utils/scheduledMessageUtils'

const PICTURE_DIMENSION = 30

const useStyles = makeStyles(theme => ({
  threadContainer: {
    textAlign: 'center',
    width: '100%',
    fontSize: '12px',
    ...Colors.darkGrey,
  },
  senderName: {
    fontWeight: 'bold',
  },
  chatMessageContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    '&.staff': {
      flexDirection: 'row-reverse',
    },
  },
  messageContent: {
    margin: `0 ${spaceSmall}px`,
    width: '100%',
  },
  avatarContainer: {
    height: PICTURE_DIMENSION,
    width: PICTURE_DIMENSION,
    flexShrink: 0,
  },
  author: {
    '& span': {
      fontSize: fontSizeSmall,
    },
  },
  scheduledMessage: {
    border: '2px dashed rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
    padding: '12px',
    backgroundColor: 'rgba(240, 240, 240, 0.5)',
    margin: '8px 0',
    position: 'relative',

    '&::before': {
      content: '"📅 Scheduled"',
      position: 'absolute',
      top: '-10px',
      left: '12px',
      backgroundColor: 'white',
      padding: '0 8px',
      fontSize: '12px',
      fontWeight: 600,
      color: 'rgba(0, 0, 0, 0.6)',
    },
  },
  scheduledInfo: {
    fontSize: '11px',
    color: 'rgba(0, 0, 0, 0.5)',
    marginTop: '4px',
    fontStyle: 'italic',
  },
  scheduledActions: {
    display: 'flex',
    gap: '4px',
    marginTop: '8px',
    justifyContent: 'flex-end',
  },
}))

const ChatMessage = forwardRef(function ChatMessage(props, ref) {
  const staffLookup = useSelector(state => state.Staff.staffLookup)
  const activeBuilding = useSelector(state => state.Buildings.buildingsLookup[state.Buildings.activeBuildingId])
  const classes = useStyles()

  if (!props.message) {
    return null
  }

  const isStaffMessage = props.message.chatUser && props.message.chatUser.isStaffUser
  const staff = isStaffMessage ? staffLookup[props.message.chatUser.userId] : null
  const staffIsDisabled = staff ? staff.isDisabled : false

  // Check if message is scheduled
  const isScheduled = props.message.isScheduled && props.message.scheduledStatus === 'PENDING'

  // Format scheduled time using shared utility
  const formatScheduledTimeForMessage = dateTime => {
    return formatScheduledTime(dateTime, activeBuilding?.timezone, {includeYear: true})
  }

  let messageRender
  if (props.message.hasAttachments) {
    // TODO: just supporting 1 image attachment at a time
    messageRender = <PictureChatMessage s3Object={props.message.attachments[0]} />
  } else {
    messageRender = (
      <EditableTextChatMessage
        message={props.message}
        isSendingMessage={props.isSendingMessage}
        onEdit={props.onEdit}
        onDelete={props.onDelete}
      />
    )
  }

  return (
    <div
      className={constructClassString(
        {
          staff: isStaffMessage,
          [classes.scheduledMessage]: isScheduled,
        },
        classes.chatMessageContainer,
        props.className,
      )}
      ref={ref}
    >
      <div className={classes.avatarContainer}>
        {isStaffMessage ? (
          <StaffProfileImage staffId={props.message.chatUser.userId} />
        ) : (
          <ResidentProfileImage residentId={props.message.chatUser.userId} hideAppStatus={true} />
        )}
      </div>
      <div className={classes.messageContent}>
        <div className={classes.author}>
          {props.message.chatUser.displayName + `${staffIsDisabled ? ', Former Staff' : ''}`}{' '}
          <span>{isStaffMessage ? 'RXO' : 'Resident'}</span>
        </div>
        {messageRender}

        {/* Scheduled message info */}
        {isScheduled && (
          <div className={classes.scheduledInfo}>Scheduled for: {formatScheduledTimeForMessage(props.message.scheduledSendAt)}</div>
        )}

        {/* Edit/Cancel actions for scheduled messages (staff only) */}
        {isScheduled && isStaffMessage && (
          <div className={classes.scheduledActions}>
            <IconButton onClick={() => props.onEditScheduled && props.onEditScheduled(props.message)} size="small" title="Edit message">
              <RXRIcon icon={RXRIcon.EDIT} size={RXRIcon.SIZE_SMALL} color={Colors.rxrPrimaryColor} />
            </IconButton>
            <IconButton
              onClick={() => props.onCancelScheduled && props.onCancelScheduled(props.message)}
              size="small"
              title="Cancel message"
            >
              <RXRIcon icon={RXRIcon.CLOSE} size={RXRIcon.SIZE_SMALL} color={Colors.rxrRedColor} />
            </IconButton>
          </div>
        )}
      </div>
    </div>
  )
})

ChatMessage.propTypes = {
  message: PropTypes.object.isRequired,
  isSendingMessage: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onEditScheduled: PropTypes.func, // Callback for editing scheduled messages
  onCancelScheduled: PropTypes.func, // Callback for canceling scheduled messages
  className: PropTypes.string,
}

export default ChatMessage
